/*
 * Enhanced Subscriptions View Component
 * 
 * Route subscription management with enhanced features:
 * - Legacy bus route subscriptions display
 * - Enhanced route subscriptions with location picker and distance settings
 * - Click-to-map navigation for pickup locations
 * - Route subscription creation with map-based location selection
 * 
 * FEATURES TO EDIT:
 * - Subscription display: Modify subscription card layout
 * - Route information: Update route details display
 * - Location picker: Customize map-based location selection
 * - Distance settings: Modify notification distance options
 * 
 * LOCATION IN CODE: Search for "SubscriptionsView" function
 */

import React, { useState, useEffect } from 'react';
import { getTimeSinceUpdate } from '../../utils/config';
import RouteSubscriptionForm from '../Forms/RouteSubscriptionForm';

/**
 * Legacy Subscription Card Component
 * Displays old-style bus route subscriptions
 */
const LegacySubscriptionCard = ({ subscription, routes, onSubscriptionClick }) => {
  const route = routes.find(r => r.id === subscription.bus_route_id);
  
  return (
    <div 
      onClick={() => onSubscriptionClick(subscription)}
      className="border rounded-lg p-4 hover:shadow-md hover:scale-105 cursor-pointer bg-blue-50 border-blue-200 transition-all duration-200"
    >
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-semibold text-gray-900">
          {route ? route.name : 'Unknown Route'}
        </h3>
        <div className="flex flex-col items-end space-y-1">
          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
            🔔 Legacy Subscription
          </span>
          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
            📍 {subscription.notification_radius} min
          </span>
        </div>
      </div>
      
      <div className="space-y-1 text-sm text-gray-600">
        <p><strong>Route:</strong> {route ? route.description : 'Route details unavailable'}</p>
        <p><strong>Pickup Location:</strong> {subscription.pickup_location.lat.toFixed(4)}, {subscription.pickup_location.lng.toFixed(4)}</p>
        <p><strong>Notification:</strong> {subscription.notification_radius} minutes before arrival</p>
        <p><strong>Subscribed:</strong> {new Date(subscription.created_at).toLocaleDateString()}</p>
        {route && (
          <p><strong>Schedule:</strong> {route.schedule_times.join(', ')}</p>
        )}
      </div>
      
      <div className="mt-3 pt-3 border-t border-blue-200">
        <p className="text-xs text-blue-600 font-medium">
          🗺️ Click to view pickup location on map
        </p>
      </div>
    </div>
  );
};

/**
 * Enhanced Route Subscription Card Component
 * Displays new-style route subscriptions with enhanced features
 */
const RouteSubscriptionCard = ({ subscription, onSubscriptionClick }) => {
  return (
    <div 
      onClick={() => onSubscriptionClick(subscription)}
      className="border rounded-lg p-4 hover:shadow-md hover:scale-105 cursor-pointer bg-green-50 border-green-200 transition-all duration-200"
    >
      <div className="flex justify-between items-start mb-3">
        <h3 className="font-semibold text-lg text-gray-900">
          {subscription.origin_town} Route
        </h3>
        <div className="flex flex-col items-end space-y-1">
          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
            🔔 Enhanced Subscription
          </span>
          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
            📍 {subscription.notification_distance_miles} miles
          </span>
        </div>
      </div>
      
      <div className="space-y-2 text-sm text-gray-600">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="font-medium text-gray-800">Origin Town:</p>
            <p>{subscription.origin_town}</p>
          </div>
          <div>
            <p className="font-medium text-gray-800">Departure Time:</p>
            <p className="font-mono">{subscription.departure_time}</p>
          </div>
        </div>
        
        <div className="pt-2 border-t border-green-200">
          <p className="font-medium text-gray-800">Pickup Location:</p>
          <p className="text-xs font-mono">
            {subscription.pickup_location.lat.toFixed(6)}, {subscription.pickup_location.lng.toFixed(6)}
          </p>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="font-medium text-gray-800">Notification Distance:</p>
            <p>{subscription.notification_distance_miles} miles from pickup</p>
          </div>
          <div>
            <p className="font-medium text-gray-800">Created:</p>
            <p>{new Date(subscription.created_at).toLocaleDateString()}</p>
          </div>
        </div>
      </div>
      
      <div className="mt-3 pt-3 border-t border-green-200">
        <p className="text-xs text-green-600 font-medium flex items-center">
          <span className="mr-2">🗺️</span>
          Click to view pickup location on map
        </p>
      </div>
    </div>
  );
};

/**
 * Enhanced Subscriptions View Component
 * Route subscription management with enhanced features
 */
const SubscriptionsView = ({ 
  subscriptions, 
  routeSubscriptions, 
  routes, 
  onRefresh, 
  onSubscriptionClick, enableSwipe, disableSwipe 
}) => {
  const [showRouteSubscriptionForm, setShowRouteSubscriptionForm] = useState(false);

  const anyModalOpen =
    showRouteSubscriptionForm;

  useEffect(() => {
      if (!enableSwipe || !disableSwipe) return;   // parent didn’t pass helpers
      anyModalOpen ? disableSwipe() : enableSwipe();
    }, [anyModalOpen, enableSwipe, disableSwipe]);

  // Calculate statistics
  const totalSubscriptions = subscriptions.length + routeSubscriptions.length;
  const legacySubscriptions = subscriptions.length;
  const enhancedSubscriptions = routeSubscriptions.length;
  const uniqueRoutes = new Set([
    ...subscriptions.map(s => s.bus_route_id),
    ...routeSubscriptions.map(s => s.origin_town)
  ]).size;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Route Subscriptions</h2>
          <p className="text-sm text-gray-600 mt-1">
            Subscribe to bus routes for arrival notifications
          </p>
        </div>
        <button
          onClick={() => setShowRouteSubscriptionForm(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
        >
          Subscribe to Route
        </button>
      </div>

      {/* Statistics Dashboard */}
      {totalSubscriptions > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-blue-600 text-2xl mr-3">🔔</div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Subscriptions</p>
                <p className="text-2xl font-bold text-blue-800">{totalSubscriptions}</p>
                <p className="text-xs text-blue-600">active notifications</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-green-600 text-2xl mr-3">⭐</div>
              <div>
                <p className="text-sm text-green-600 font-medium">Enhanced</p>
                <p className="text-2xl font-bold text-green-800">{enhancedSubscriptions}</p>
                <p className="text-xs text-green-600">with distance alerts</p>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-purple-600 text-2xl mr-3">🛣️</div>
              <div>
                <p className="text-sm text-purple-600 font-medium">Unique Routes</p>
                <p className="text-2xl font-bold text-purple-800">{uniqueRoutes}</p>
                <p className="text-xs text-purple-600">different routes</p>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-yellow-600 text-2xl mr-3">📱</div>
              <div>
                <p className="text-sm text-yellow-600 font-medium">Legacy</p>
                <p className="text-2xl font-bold text-yellow-800">{legacySubscriptions}</p>
                <p className="text-xs text-yellow-600">old subscriptions</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* No Subscriptions State */}
      {totalSubscriptions === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔔</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No route subscriptions yet</h3>
          <p className="text-gray-600 mb-4">
            Subscribe to bus routes to get arrival notifications
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Get notified when buses are approaching your pickup location
          </p>
          <button
            onClick={() => setShowRouteSubscriptionForm(true)}
            className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors"
          >
            Create Your First Subscription
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Enhanced Route Subscriptions */}
          {enhancedSubscriptions > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Enhanced Route Subscriptions ({enhancedSubscriptions})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {routeSubscriptions.map((subscription) => (
                  <RouteSubscriptionCard
                    key={subscription.id}
                    subscription={subscription}
                    onSubscriptionClick={onSubscriptionClick}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Legacy Bus Route Subscriptions */}
          {legacySubscriptions > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Legacy Route Subscriptions ({legacySubscriptions})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {subscriptions.map((subscription) => (
                  <LegacySubscriptionCard
                    key={subscription.id}
                    subscription={subscription}
                    routes={routes}
                    onSubscriptionClick={onSubscriptionClick}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions and Tips */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-2">📱 How Route Subscriptions Work:</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <p>• <strong>Enhanced subscriptions</strong> use distance-based notifications (miles from pickup)</p>
          <p>• <strong>Legacy subscriptions</strong> use time-based notifications (minutes before arrival)</p>
          <p>• <strong>Click subscription cards</strong> to view pickup locations on the map</p>
          <p>• <strong>Real-time notifications</strong> are sent when buses approach your area</p>
        </div>
      </div>

      {/* Route Subscription Form Modal */}
      {showRouteSubscriptionForm && (
        <RouteSubscriptionForm
          onClose={() => setShowRouteSubscriptionForm(false)}
          onSubmit={() => {
            setShowRouteSubscriptionForm(false);
            onRefresh();
          }}
        />
      )}
    </div>
  );
};

export default SubscriptionsView;